import { useMemo, useState } from 'react';
import type { CoverageEntitiesResult, DemographicBreakdown, RouteMetricsResult } from '../api';
import { formatPct, formatPopulation } from '../summaryFormat';
import { SECONDARY_TYPE_LABELS, type SelectedFeature } from './layers';

export type AnalyticsTab = 'poblacion' | 'equipamientos' | 'detalle';

const AGE_BRACKET_LABELS: Array<{ key: keyof DemographicBreakdown['ageBrackets']; label: string }> = [
  { key: 'pop_0_3', label: '0–3' },
  { key: 'pop_4_5', label: '4–5' },
  { key: 'pop_6_12', label: '6–12' },
  { key: 'pop_13_17', label: '13–17' },
  { key: 'pop_18_29', label: '18–29' },
  { key: 'pop_30_54', label: '30–54' },
  { key: 'pop_55_69', label: '55–69' },
  { key: 'pop_70_plus', label: '70+' },
];

const EDUCATION_LABELS: Array<{ key: keyof DemographicBreakdown['education']; label: string }> = [
  { key: 'edu_primary_incomplete', label: 'Primario incomp.' },
  { key: 'edu_primary_complete_plus', label: 'Primario+' },
  { key: 'edu_secondary_complete_plus', label: 'Secundario+' },
  { key: 'edu_tertiary_complete_no_uni', label: 'Terciario' },
  { key: 'edu_university_complete', label: 'Universitario' },
];

/** Barras verticales compactas — todas las franjas de un grupo visibles a la vez. Un solo componente reusado por edad y por educación. */
function HorizontalBarChart({
  bars,
  fillClassName = 'lop-hbar-fill',
}: {
  bars: Array<{ key: string; label: string; value: number }>;
  fillClassName?: string;
}): JSX.Element {
  const max = Math.max(1, ...bars.map(b => b.value));
  return (
    <div className="lop-hbar-chart">
      {bars.map(b => (
        <div key={b.key} className="lop-hbar-col">
          <span className="lop-hbar-value">{formatPopulation(b.value)}</span>
          <div className="lop-hbar-track">
            <div className={fillClassName} style={{ height: `${(b.value / max) * 100}%` }} />
          </div>
          <span className="lop-hbar-label">{b.label}</span>
        </div>
      ))}
    </div>
  );
}

function AgePyramid({ ageBrackets }: { ageBrackets: DemographicBreakdown['ageBrackets'] }): JSX.Element {
  return <HorizontalBarChart bars={AGE_BRACKET_LABELS.map(b => ({ key: b.key, label: b.label, value: ageBrackets[b.key] }))} />;
}

function PopStat({ value, label }: { value: string; label: string }): JSX.Element {
  return (
    <div className="lop-pop-stat">
      <span className="lop-pop-stat-value">{value}</span>
      <span className="lop-pop-stat-label">{label}</span>
    </div>
  );
}

/**
 * Todo en una sola fila (grilla de 4 columnas) — el usuario pidió
 * explícitamente ver población/edad/educación/hogares al mismo tiempo,
 * sin scroll vertical, no cada bloque apilado uno debajo del otro.
 */
function PoblacionTab({
  populationCovered,
  populationCoveragePct,
  demographics,
}: {
  populationCovered: number;
  populationCoveragePct: number | null;
  demographics: DemographicBreakdown | null;
}): JSX.Element {
  if (!demographics) {
    return (
      <div>
        <div className="ft-kpi-row">
          <div className="ft-kpi">
            <span className="ft-kpi-value">{formatPopulation(populationCovered)}</span>
            <span className="ft-kpi-label">Habitantes cubiertos</span>
          </div>
          <div className="ft-kpi">
            <span className="ft-kpi-value">{formatPct(populationCoveragePct)}</span>
            <span className="ft-kpi-label">% de la población del proyecto</span>
          </div>
        </div>
        <p className="cov-analytics-empty">Calculando…</p>
      </div>
    );
  }

  return (
    <div className="lop-pop-grid">
      <div className="lop-pop-col">
        <p className="lop-pop-col-title">Población</p>
        <div className="lop-pop-stats-grid">
          <PopStat value={formatPopulation(populationCovered)} label="Habitantes" />
          <PopStat value={formatPct(populationCoveragePct)} label="% proyecto" />
          <PopStat value={formatPopulation(demographics.sex.males)} label="Varones" />
          <PopStat value={formatPopulation(demographics.sex.females)} label="Mujeres" />
        </div>
      </div>

      <div className="lop-pop-col lop-pop-col-chart">
        <p className="lop-pop-col-title" title="Ponderado por la misma fracción longitud-en-radio ya auditada para población cubierta (census_radio_person_indicators_2022) — no un dato nuevo, la misma metodología con más columnas.">
          Estructura etaria
        </p>
        <AgePyramid ageBrackets={demographics.ageBrackets} />
      </div>

      <div className="lop-pop-col lop-pop-col-chart">
        <p className="lop-pop-col-title" title="Niveles acumulativos, no mutuamente excluyentes — no suman 100%.">
          Nivel educativo (acum.)
        </p>
        <HorizontalBarChart
          bars={EDUCATION_LABELS.map(e => ({ key: e.key, label: e.label, value: demographics.education[e.key] }))}
          fillClassName="lop-hbar-fill lop-hbar-fill-edu"
        />
      </div>

      <div className="lop-pop-col">
        <p className="lop-pop-col-title">Hogares</p>
        <div className="lop-pop-stats-grid">
          <PopStat value={formatPopulation(demographics.households.households_total)} label="Total" />
          <PopStat value={formatPopulation(demographics.households.households_with_nbi)} label="Con NBI" />
          <PopStat value={formatPopulation(demographics.households.households_overcrowding)} label="Hacinamiento" />
          <PopStat value={formatPopulation(demographics.households.households_owner)} label="Propia" />
          <PopStat value={formatPopulation(demographics.households.households_tenant)} label="Alquiler" />
        </div>
      </div>
    </div>
  );
}

function EquipamientosTab({
  coverageEntities,
  onSelect,
}: {
  coverageEntities: CoverageEntitiesResult | null;
  onSelect: (f: SelectedFeature) => void;
}): JSX.Element {
  if (!coverageEntities) return <p className="cov-analytics-empty">Calculando…</p>;
  const groups: Array<{ label: string; items: Array<{ id: string; name: string | null; osmSubtype: string | null }> }> = [
    { label: 'Educación', items: coverageEntities.education.features },
    { label: 'Salud', items: coverageEntities.health.features },
    { label: 'Cívico / público', items: coverageEntities.civic.features },
    { label: 'Espacios verdes', items: coverageEntities.greenSpace.features.filter(f => f.tier !== 'GEOMETRICALLY_NEAR') },
    { label: 'Deporte', items: coverageEntities.sportsFacility.features.filter(f => f.tier !== 'GEOMETRICALLY_NEAR') },
    {
      label: 'Otros equipamientos / interés urbano',
      items: coverageEntities.secondary.features.map(f => ({ id: f.id, name: f.name, osmSubtype: f.osmSubtype })),
    },
  ];
  return (
    <div>
      <div className="lop-equip-counts">
        {groups.map(g => (
          <div key={g.label} className="lop-equip-count-chip">
            <span className="lop-equip-count-n">{g.items.length}</span> {g.label}
          </div>
        ))}
      </div>
      {groups.map(g => (
        <div key={g.label} style={{ marginBottom: 14 }}>
          <p className="ft-block-title">
            {g.label} ({g.items.length})
          </p>
          {g.items.length === 0 ? (
            <p className="cov-analytics-empty">Ninguno dentro de la cobertura actual.</p>
          ) : (
            <div className="cov-entity-grid">
              {g.items.map(item => (
                <div
                  key={item.id}
                  className="cov-entity-card"
                  onClick={() => onSelect({ kind: 'point', category: g.label, name: item.name, osmSubtype: item.osmSubtype })}
                >
                  <div className="cov-entity-card-category">{item.osmSubtype ?? g.label}</div>
                  <div className="cov-entity-card-name">{item.name ?? 'Sin nombre'}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

interface DetailRow {
  id: string;
  category: string;
  name: string | null;
  osmSubtype: string | null;
  universe: 'Recorrido' | 'Cobertura';
  selectable: SelectedFeature;
}

function DetalleTerritorialTab({ rows, onSelect }: { rows: DetailRow[]; onSelect: (f: SelectedFeature) => void }): JSX.Element {
  const [universeFilter, setUniverseFilter] = useState<'all' | 'Recorrido' | 'Cobertura'>('all');
  const categories = useMemo(() => Array.from(new Set(rows.map(r => r.category))).sort(), [rows]);
  const [categoryFilter, setCategoryFilter] = useState('');

  const filtered = rows.filter(
    r => (universeFilter === 'all' || r.universe === universeFilter) && (!categoryFilter || r.category === categoryFilter)
  );

  return (
    <div>
      <div className="cov-filter-row">
        <select value={universeFilter} onChange={e => setUniverseFilter(e.target.value as typeof universeFilter)}>
          <option value="all">Todas ({rows.length})</option>
          <option value="Recorrido">Sobre el recorrido</option>
          <option value="Cobertura">Dentro de la cobertura</option>
        </select>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
          <option value="">Todas las categorías</option>
          {categories.map(c => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
      {filtered.length === 0 ? (
        <p className="cov-analytics-empty">Sin entidades para este filtro.</p>
      ) : (
        <table className="cov-entity-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Categoría</th>
              <th>Subtipo</th>
              <th>Universo</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={`${r.category}-${r.id}`} onClick={() => onSelect(r.selectable)}>
                <td>{r.name ?? <i>Sin nombre</i>}</td>
                <td>{r.category}</td>
                <td>{r.osmSubtype ?? '—'}</td>
                <td>{r.universe}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Semáforos/reductores/cruces se movieron a la pestaña Infraestructura
 * vial (con su propia tabla/mapa) — Detalle territorial de Cobertura ya
 * no los lista, solo lo que sigue siendo de Cobertura (pasos ferroviarios,
 * luminarias, y todo lo de "dentro de la cobertura").
 */
export function buildDetailRows(routeMetrics: RouteMetricsResult | null, coverageEntities: CoverageEntitiesResult | null): DetailRow[] {
  if (!routeMetrics) return [];
  const rows: DetailRow[] = [];
  const routeCats: Array<{ key: 'railwayCrossings' | 'streetlights'; label: string }> = [
    { key: 'railwayCrossings', label: 'Pasos ferroviarios' },
    { key: 'streetlights', label: 'Luminarias' },
  ];
  for (const c of routeCats) {
    for (const f of routeMetrics[c.key].features) {
      rows.push({
        id: f.id,
        category: c.label,
        name: f.name,
        osmSubtype: f.osmSubtype,
        universe: 'Recorrido',
        selectable: { kind: 'point', category: c.label, name: f.name, osmSubtype: f.osmSubtype, hasNetworkAssociation: f.hasNetworkAssociation },
      });
    }
  }
  if (coverageEntities) {
    const coverageCats: Array<{ key: 'education' | 'health' | 'civic'; label: string }> = [
      { key: 'education', label: 'Educación' },
      { key: 'health', label: 'Salud' },
      { key: 'civic', label: 'Cívico / público' },
    ];
    for (const c of coverageCats) {
      for (const f of coverageEntities[c.key].features) {
        rows.push({
          id: f.id,
          category: c.label,
          name: f.name,
          osmSubtype: f.osmSubtype,
          universe: 'Cobertura',
          selectable: { kind: 'point', category: c.label, name: f.name, osmSubtype: f.osmSubtype, hasNetworkAssociation: f.hasNetworkAssociation },
        });
      }
    }
    for (const [areaKey, label] of [
      ['greenSpace', 'Espacios verdes'],
      ['sportsFacility', 'Deporte'],
    ] as const) {
      for (const f of coverageEntities[areaKey].features) {
        if (f.tier === 'GEOMETRICALLY_NEAR') continue;
        rows.push({
          id: f.id,
          category: label,
          name: f.name,
          osmSubtype: f.osmSubtype,
          universe: 'Cobertura',
          selectable: { kind: 'area', category: label, name: f.name, osmSubtype: f.osmSubtype, tier: f.tier },
        });
      }
    }
    for (const f of coverageEntities.secondary.features) {
      const label = SECONDARY_TYPE_LABELS[f.entityType] ?? f.entityType;
      rows.push({
        id: f.id,
        category: label,
        name: f.name,
        osmSubtype: f.osmSubtype,
        universe: 'Cobertura',
        selectable: { kind: 'point', category: label, name: f.name, osmSubtype: f.osmSubtype, hasNetworkAssociation: false },
      });
    }
  }
  return rows;
}

/**
 * Cajón inferior expandible de Cobertura — handle en el borde del mapa,
 * 3 pestañas (Población/Equipamientos/Detalle). Trayecto se movió a la
 * pestaña Infraestructura vial (junto con semáforos/reductores/cruces,
 * que dejaron de pertenecer a Cobertura). Cerrado el mapa ocupa casi toda
 * la pantalla; abierto despliega esto sin navegar.
 */
export function BottomAnalyticsSheet({
  open,
  onToggleOpen,
  activeTab,
  onTabChange,
  coverageEntities,
  detailRows,
  onSelect,
}: {
  open: boolean;
  onToggleOpen: () => void;
  activeTab: AnalyticsTab;
  onTabChange: (t: AnalyticsTab) => void;
  coverageEntities: CoverageEntitiesResult | null;
  detailRows: DetailRow[];
  onSelect: (f: SelectedFeature) => void;
}): JSX.Element {
  return (
    <div className={open ? 'lop-sheet lop-sheet-open' : 'lop-sheet'}>
      <button className="lop-sheet-handle" onClick={onToggleOpen} aria-label={open ? 'Cerrar analítica' : 'Abrir analítica'}>
        <span className="lop-sheet-handle-arrow">{open ? '⌄' : '⌃'}</span>
        {!open && <span className="lop-sheet-handle-label">Ver análisis de cobertura</span>}
      </button>
      {open && (
        <>
          <div className="cov-analytics-tabs">
            {(
              [
                ['poblacion', 'Población'],
                ['equipamientos', 'Equipamientos'],
                ['detalle', 'Detalle territorial'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                className={activeTab === key ? 'cov-analytics-tab cov-analytics-tab-active' : 'cov-analytics-tab'}
                onClick={() => onTabChange(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="cov-analytics-body">
            {activeTab === 'poblacion' && coverageEntities && (
              <PoblacionTab
                populationCovered={coverageEntities.populationCovered}
                populationCoveragePct={coverageEntities.populationCoveragePct}
                demographics={coverageEntities.demographics}
              />
            )}
            {activeTab === 'poblacion' && !coverageEntities && <p className="cov-analytics-empty">Calculando…</p>}
            {activeTab === 'equipamientos' && <EquipamientosTab coverageEntities={coverageEntities} onSelect={onSelect} />}
            {activeTab === 'detalle' && <DetalleTerritorialTab rows={detailRows} onSelect={onSelect} />}
          </div>
        </>
      )}
    </div>
  );
}
